import React, { useState, ChangeEvent, FormEvent } from "react";
import emailjs from "emailjs-com";
import { storage } from "../firebaseConfig";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const EnquiryForm2 = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    orderDescription: "",
    deadline: "",
    agree: false,
    file: null as File | null,
  });

  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    if (type === "checkbox") {
      const { checked } = e.target as HTMLInputElement;
      setFormData({ ...formData, [name]: checked });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFormData({ ...formData, file: e.target.files[0] });
    }
  };

  const validateForm = (): boolean => {
    const { name, email, phone, service, orderDescription, deadline, agree } = formData;
    if (!name) {
      toast.error("Please enter your name");
      return false;
    }
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email || !emailPattern.test(email)) {
      toast.error("Please enter a valid email address");
      return false;
    }
    const phonePattern = /^[0-9]+$/;
    if (!phone || !phonePattern.test(phone)) {
      toast.error("Please enter a valid phone number");
      return false;
    }
    if (!service) {
      toast.error("Please select a service");
      return false;
    }
    if (!orderDescription) {
      toast.error("Please enter an order description");
      return false;
    }
    if (!deadline) {
      toast.error("Please select a deadline");
      return false;
    }
    if (!agree) {
      toast.error("You must accept the terms and conditions");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsLoading(true);

    const { name, email, phone, service, orderDescription, deadline, agree, file } = formData;

    let fileURL = "No file attached";
    if (file) {
      const fileRef = ref(storage, `enquiries/${file.name}`);
      await uploadBytes(fileRef, file);
      fileURL = await getDownloadURL(fileRef);
    }

    const templateParams = {
      name, email, phone, service, orderDescription, deadline,
      agree: agree ? "Yes" : "No",
      file: fileURL,
    };

    const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID || "";
    const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID || "";
    const userId = process.env.REACT_APP_EMAILJS_USER_ID || "";
    emailjs.init(userId);

    emailjs.send(serviceId, templateId, templateParams, userId)
      .then(
        (response) => {
          console.log("SUCCESS!", response.status, response.text);
          toast.success("Enquiry sent successfully!");
          setFormData({
            name: "", email: "", phone: "", service: "",
            orderDescription: "", deadline: "", agree: false, file: null,
          });
        },
        (err) => {
          console.log("FAILED...", err);
          toast.error("Failed to send enquiry. Please try again.");
        }
      )
      .finally(() => setIsLoading(false));
  };

  return (
    <div className="w-full max-w-sm mx-auto px-2 sm:px-0">
      <form
        onSubmit={handleSubmit}
        className="p-3 border rounded-lg shadow-md"
        style={{ position: "relative", backgroundColor: "rgba(255,255,255,0.7)" }}
      >
        <div className="form-head2">
          <h2 className="text-xl font-bold text-orange-500 mb-2 text-left ms-2 ps-4" style={{lineHeight:"60px"}}>
            Enquiry Form
          </h2>
        </div>

        <div className="space-y-2">
          <input
            type="text"
            name="name"
            placeholder="Fullname"
            value={formData.name}
            onChange={handleChange}
            className="w-full py-1 px-2 font-normal border bg-white rounded-lg text-sm"
            style={{ outlineColor: "rgb(231 111 28)", color: "black" }}
            required
          />

          <input
            type="email"
            name="email"
            placeholder="Email"
            value={formData.email}
            onChange={handleChange}
            className="w-full py-1 px-2 font-normal border bg-white rounded-lg text-sm"
            style={{ outlineColor: "rgb(231 111 28)", color: "black" }}
            required
          />

          <input
            type="tel"
            name="phone"
            placeholder="Phone no."
            value={formData.phone}
            onChange={handleChange}
            className="w-full py-1 px-2 border font-normal bg-white rounded-lg text-sm"
            style={{ outlineColor: "rgb(231 111 28)" }}
            required
          />

          <div>
            <label htmlFor="service" className="font-normal text-sm">Select a service:</label>
            <select
              name="service"
              value={formData.service}
              onChange={handleChange}
              className="w-full rounded-lg text-sm"
              style={{ 
                padding: '4px 8px', 
                borderRadius: '10px', 
                backgroundColor: "rgba(35, 65, 82)", 
                color: 'white', 
                fontSize: "14px", 
                outline: "none" 
              }}
            >
              <option value="">Select a service</option>
              <option value="review_article">Review Article Writing/ Editing</option>
              <option value="research_article">Research Article Writing/ Editing</option>
              <option value="plagiarism_free">Plagiarism Free Writing</option>
              <option value="proofreading">Proofreading</option>
              <option value="projects_development">Projects Development</option>
              <option value="content_writing">Content Writing / Editing</option>
              <option value="survey_analysis">Survey / Data Analysis</option>
              <option value="translation">Article Translation</option>
            </select>
          </div>

          <div>
            <textarea
              name="orderDescription"
              placeholder="Order Description (Write/Attach)"
              value={formData.orderDescription}
              onChange={handleChange}
              className="w-full py-1 px-2 font-normal border bg-white rounded-lg text-sm"
              style={{ outlineColor: "rgb(231 111 28)", height: "60px" }}
              required
            />
            <input
              type="file"
              name="file"
              onChange={handleFileChange}
              className="w-full font-normal mt-1 text-sm"
            />
          </div>

          <div>
            <label htmlFor="deadline" className="font-normal text-sm">Deadline:</label>
            <input
              type="date"
              name="deadline"
              value={formData.deadline}
              onChange={handleChange}
              className="w-full py-1 px-2 border font-normal bg-white rounded-lg text-sm"
              style={{ outlineColor: "rgb(231 111 28)" }}
              required
            />
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              name="agree"
              checked={formData.agree}
              onChange={handleChange}
              className="mr-2 bg-white font-normal"
              style={{ outlineColor: "rgb(231 111 28)" }}
              required
            />
            <label className="font-normal text-sm">
              I accept the T&C, agree to receive offers & updates
            </label>
          </div>
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="w-full py-1.5 px-2 mt-2 bg-orange-500 text-white font-bold rounded-lg hover:bg-orange-700 text-sm"
        >
          {isLoading ? "Sending..." : "Send Enquiry"}
        </button>
      </form>
      <ToastContainer />
    </div>
  );
};

export default EnquiryForm2;